import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, Paper, Stack, TableContainer, Table, TableRow, TableHead, TableCell, TableBody, TextField, InputAdornment, Box } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { isEmpty, isUndefined } from 'lodash';
import { DataContext } from '../../../context/DataContext';
import { getRuta, isAccess, simpleMessage } from '../../../helpers/Helpers';
import { getToken } from '../../../api/Account';
import NoData from '../../../components/NoData';
import { PaginationComponent } from '../../../components/PaginationComponent';
import { getArqueoUserBySucursalAsync } from '../../../api/ArqueoCaja';
import StatusChip from '../../../components/ui/StatusChip';
import { monoFont } from '../../../theme';
import moment from 'moment';

const money = (n) => new Intl.NumberFormat('es-NI', { style: 'currency', currency: 'NIO' }).format(n);

const estadoInfo = (estadoArqueo) => {
	if (estadoArqueo === 2) {
		return { label: 'Cerrado', tone: 'success' };
	}
	if (estadoArqueo === 1) {
		return { label: 'Abierto', tone: 'warning' };
	}
	return { label: 'En revisión', tone: 'info' };
};

const ArqueoUser = () => {
	const [arqueoList, setArquoList] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedFecha, setSelectedFecha] = useState(moment().startOf('month').toDate());

	const { setIsLoading, setIsUnautorized, access, selectedSucursal } = useContext(DataContext);

	let ruta = getRuta();
	const token = getToken();
	let navigate = useNavigate();

	const withSearch = arqueoList.filter((val) => {
		if (searchTerm === '') {
			return val;
		} else if (val.gestor.includes(searchTerm)) {
			return val;
		}
		return false;
	});

	const onChangeSearch = (val) => {
		setCurrentPage(1);
		setSearchTerm(val);
		paginate(1);
	};

	useEffect(() => {
		if (selectedSucursal === 0 || isUndefined(selectedSucursal)) {
			return;
		}
		getArqueoUser(selectedSucursal, selectedFecha);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSucursal, selectedFecha]);

	const [currentPage, setCurrentPage] = useState(1);
	const [itemsperPage] = useState(10);
	const indexLast = currentPage * itemsperPage;
	const indexFirst = indexLast - itemsperPage;
	const currentItem = withSearch.slice(indexFirst, indexLast);
	const paginate = (pageNumber) => setCurrentPage(pageNumber);

	const getArqueoUser = async (sucursal, fecha) => {
		setIsLoading(true);
		const result = await getArqueoUserBySucursalAsync(token, sucursal, moment(fecha).format('YYYY-MM-DD'));
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);

		setArquoList(result.data.result);
	};

	const onChangeFecha = (value) => {
		setSelectedFecha(value);
	};

	return (
		<div>
			<Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ marginBottom: '18px' }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
					<DesktopDatePicker label="Desde" value={selectedFecha} onChange={onChangeFecha} renderInput={(params) => <TextField size="small" {...params} sx={{ minWidth: 180 }} />} />
					<TextField
						size="small"
						onChange={(e) => onChangeSearch(e.target.value.toUpperCase())}
						value={searchTerm}
						placeholder="Buscar gestor..."
						sx={{ width: { xs: '100%', sm: 220 } }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchRoundedIcon sx={{ fontSize: 19, color: 'text.disabled' }} />
								</InputAdornment>
							)
						}}
					/>
				</Stack>

				{isAccess(access, 'ACAJA CREATE') ? (
					<Button onClick={() => navigate(`${ruta}/arqueouser/add/${selectedSucursal}`)} startIcon={<AddRoundedIcon />} variant="contained" color="primary" sx={{ whiteSpace: 'nowrap' }}>
						Agregar arqueo
					</Button>
				) : null}
			</Stack>

			{isEmpty(currentItem) ? (
				<NoData />
			) : (
				<Paper sx={{ overflow: 'hidden' }}>
					<TableContainer sx={{ overflowX: 'auto' }}>
						<Table size="small" sx={{ minWidth: 1000 }}>
							<TableHead>
								<TableRow>
									<TableCell>#</TableCell>
									<TableCell>F. Creación</TableCell>
									<TableCell>F. Arqueo</TableCell>
									<TableCell>Gestor</TableCell>
									<TableCell align="right">Entrega</TableCell>
									<TableCell align="right">Efectivo</TableCell>
									<TableCell align="right">Diferencia</TableCell>
									<TableCell>Recibido</TableCell>
									<TableCell align="center">Estado</TableCell>
									{isAccess(access, 'ACAJA UPDATE') ? <TableCell align="center">Ver</TableCell> : null}
								</TableRow>
							</TableHead>

							<TableBody>
								{currentItem.map((row) => {
									const { diferencia, fechaCreacion, fechaArqueo, recibidoPor, gestor, entrega, totalEfectivo, id, estadoArqueo } = row;
									const estado = estadoInfo(estadoArqueo);
									return (
										<TableRow key={id}>
											<TableCell sx={{ fontFamily: monoFont, color: 'text.disabled' }}>{id}</TableCell>
											<TableCell sx={{ fontFamily: monoFont, fontSize: 11.5 }}>{moment(fechaCreacion).format('L')}</TableCell>
											<TableCell sx={{ fontFamily: monoFont, fontSize: 11.5 }}>{moment(fechaArqueo).format('L')}</TableCell>
											<TableCell sx={{ fontSize: 12.5, fontWeight: 500 }}>{gestor}</TableCell>
											<TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 12 }}>
												{money(entrega)}
											</TableCell>
											<TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 12 }}>
												{money(totalEfectivo)}
											</TableCell>
											<TableCell align="right" sx={{ fontFamily: monoFont, fontSize: 12, fontWeight: 600, color: diferencia < 0 ? 'error.main' : diferencia > 0 ? 'success.main' : 'text.secondary' }}>
												{money(diferencia)}
											</TableCell>
											<TableCell sx={{ fontSize: 12 }}>{recibidoPor}</TableCell>
											<TableCell align="center">
												<StatusChip label={estado.label} tone={estado.tone} />
											</TableCell>
											{isAccess(access, 'ACAJA UPDATE') ? (
												<TableCell align="center">
													<IconButton size="small" onClick={() => navigate(`${ruta}/arqueouser/details/${row.id}`)}>
														<OpenInNewRoundedIcon sx={{ fontSize: 17 }} />
													</IconButton>
												</TableCell>
											) : null}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>

					<Box sx={{ padding: '10px 20px 14px' }}>
						<PaginationComponent data={withSearch} paginate={paginate} itemsperPage={itemsperPage} />
					</Box>
				</Paper>
			)}
		</div>
	);
};

export default ArqueoUser;
